/** Rotates editable announcements while keeping contact links stationary. */
class CustomAnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.slides = [...this.querySelectorAll('.custom-announcement-slide')];
    this.index = 0;
    this.animations = [];
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.hovered = this.focused = false;
    const bar = this.closest('.custom-announcement-bar') || this;
    bar.addEventListener('pointerenter', () => { this.hovered = true; this.sync(); }, options);
    bar.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, options);
    bar.addEventListener('focusin', () => { this.focused = true; this.sync(); }, options);
    bar.addEventListener('focusout', (event) => { this.focused = bar.contains(event.relatedTarget); this.sync(); }, options);
    this.motion.addEventListener('change', () => this.sync(), options);
    document.addEventListener('visibilitychange', () => this.sync(), options);
    this.sync();
  }

  sync() {
    clearTimeout(this.timer);
    if (this.motion.matches && this.running) { this.animations.forEach(animation => animation.cancel()); return; }
    const paused = this.hovered || this.focused || document.hidden || this.motion.matches;
    for (const animation of this.animations) {
      if (paused) animation.pause(); else animation.play();
    }
    if (!paused && !this.running && this.dataset.effect !== 'none' && this.slides.length) {
      this.timer = setTimeout(() => this.next(), Math.max(3, Number(this.dataset.interval) || 5) * 1000);
    }
  }

  async next() {
    this.running = true;
    const current = this.slides[this.index];
    const nextIndex = (this.index + 1) % this.slides.length;
    const incoming = this.slides[nextIndex];
    const vertical = this.dataset.effect === 'vertical';
    const offset = vertical ? 'translateY' : 'translateX';
    const distance = vertical ? '100%' : '32px';
    const timing = { duration: Number(this.dataset.duration) || 400, easing: 'cubic-bezier(.22,.61,.36,1)' };
    try {
      this.animations = [current.animate([{ transform:`${offset}(0)`, opacity:1 }, { transform:`${offset}(-${distance})`, opacity:0 }], timing)];
      await this.animations[0].finished;
      delete current.dataset.active;
      current.setAttribute('aria-hidden', 'true'); current.inert = true;
      incoming.dataset.active = '';
      incoming.setAttribute('aria-hidden', 'false'); incoming.inert = false;
      this.index = nextIndex;
      this.animations = [incoming.animate([{ transform:`${offset}(${distance})`, opacity:0 }, { transform:`${offset}(0)`, opacity:1 }], timing)];
      await this.animations[0].finished;
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Announcement animation:', error);
    } finally {
      this.animations = [];
      this.running = false;
      if (this.isConnected && !this.controller.signal.aborted) this.sync();
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.timer);
    this.animations?.forEach(animation => animation.cancel());
  }
}
if (!customElements.get('custom-announcement-rotator')) customElements.define('custom-announcement-rotator', CustomAnnouncementRotator);
